<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div v-if="!error" ref="dashboard" class="dashboard-root">
        <!-- TITLE AND ACTION -->
        <header id="dashboard-header" :style="{ padding: '0 ' + gutter }">
            <h1 class="mb-0">{{ $l10n.__('Bonjour %(user)s', { user: user }) }}</h1>
            <div class="flex-item-right">
                <CompassButton
                    :label="dashboardManagementLabel"
                    type="primary"
                    data-bot="dashboard-manage"
                    @click.stop="togglePanelSettings"
                />
            </div>
        </header>

        <div v-if="!loading" class="prelative">
            <grid-layout
                ref="grid"
                :layout="layout"
                :col-num="col"
                :row-height="160"
                :is-draggable="true"
                :responsive="true"
                :margin="[gutter, gutter]"
                :breakpoints="breakpoints"
                :cols="cols"
                @layout-updated="layoutUpdated"
                @update:layout="layoutUpdated"
            >
                <grid-item
                    v-for="item in layout"
                    :key="item.type"
                    drag-allow-from=".card-header"
                    :x="item.x"
                    :y="item.y"
                    :w="item.w"
                    :h="item.h"
                    :i="item.i"
                    :is-resizable="item.resizable"
                    :min-w="item.minw"
                    :min-h="item.minh"
                    :max-w="item.maxw"
                    :max-h="item.maxh"
                    @resized="resizeEvent"
                    @moved="layoutUpdated"
                >
                    <template v-if="getCard(item.type)">
                        <component
                            :is="getTypeCard(item.type) + '-card'"
                            :ref="item.type"
                            :key="item.type"
                            :settings="getCard(item.type).settings"
                            :custom-settings="getCard(item.type).customSettings"
                            :name="getCard(item.type).name"
                            :type="getTypeCard(item.type)"
                            :loading="item.loading"
                            :data="getCard(item.type).content"
                            :error="getCard(item.type).error"
                            :timeout="timeout"
                            @hide-card="removeItem"
                            @fetched="cardFetchedEvent"
                            @set-monitored-report-cards="setMonitoredReportCards"
                        >
                        </component>
                    </template>
                </grid-item>
            </grid-layout>
            <cards-settings-panel
                v-if="openPanelSettings"
                ref="cardSettingsPanel"
                :items="cardsForSettings"
                :default-opened-tab="defaultOpenedTab"
                :max="max"
                :min="min"
                :default-report-card-limit="reportCardLimit"
                @close="togglePanelSettings"
                @add="addItem"
                @removed="removeItem"
                @save="applyButton"
            />
        </div>
        <preloader v-else :background="false" :display="true" />
        <portal-target name="dashboardSettingsForm" :multiple="true"> </portal-target>

        <RecapDialog v-if="showRecapDialog" :visible.sync="isRecapDialogVisible" />
    </div>
</template>

<script>
import VueGridLayout from 'vue-grid-layout';
import AgendaCard from './cards/AgendaCard.vue';
import OpportunityCard from './cards/OpportunityCard.vue';
import TicketCorpCard from './cards/TicketCorpCard.vue';
import TicketCard from './cards/TicketCard.vue';
import TaskCard from './cards/TaskCard.vue';
import TurnoverStaffCard from './cards/TurnoverStaffCard.vue';
import TurnoverCorpCard from './cards/TurnoverCorpCard.vue';
import EstimateCorpCard from './cards/EstimateCorpCard.vue';
import EstimateStaffCard from './cards/EstimateStaffCard.vue';
import InvoiceCorpCard from './cards/InvoiceCorpCard.vue';
import InvoiceStaffCard from './cards/InvoiceStaffCard.vue';
import OrderCorpCard from './cards/OrderCorpCard.vue';
import OrderStaffCard from './cards/OrderStaffCard.vue';
import PurInvoiceCard from './cards/PurInvoiceCard.vue';
import ActivityCard from './cards/ActivityCard.vue';
import ProformaCorpCard from './cards/ProformaCorpCard.vue';
import ProformaStaffCard from './cards/ProformaStaffCard.vue';
import MarketingCampaignCard from './cards/MarketingCampaignCard.vue';
import AnnotationCard from './cards/AnnotationCard.vue';
import LastObjectCard from './cards/LastObjectCard.vue';
import BankAccountBalanceCard from './cards/BankAccountBalanceCard.vue';
import InvoiceUnreconciledCard from './cards/InvoiceUnreconciledCard.vue';
import MyActivitiesCard from './cards/MyActivitiesCard.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import { createNamespacedHelpers } from 'vuex';
import CardsSettingsPanel from 'vcomponents/dashboard/CardsSettingsPanel.vue';
import ReportCard from './cards/ReportCard.vue';
import RecapDialog from 'vcomponents/recap/RecapDialog.vue';
import Feature from 'Services/Feature';

const { mapState } = createNamespacedHelpers('user');

export default {
    components: {
        Preloader,
        GridLayout: VueGridLayout.GridLayout,
        GridItem: VueGridLayout.GridItem,
        CardsSettingsPanel,
        'agenda-card': AgendaCard,
        'opportunity-card': OpportunityCard,
        'ticket_corp-card': TicketCorpCard,
        'ticket-card': TicketCard,
        'task-card': TaskCard,
        'turnover_staff-card': TurnoverStaffCard,
        'turnover_corp-card': TurnoverCorpCard,
        'estimate_staff-card': EstimateStaffCard,
        'estimate_corp-card': EstimateCorpCard,
        'invoice_staff-card': InvoiceStaffCard,
        'invoice_corp-card': InvoiceCorpCard,
        'order_staff-card': OrderStaffCard,
        'order_corp-card': OrderCorpCard,
        'pur_invoice-card': PurInvoiceCard,
        'activity-card': ActivityCard,
        'proforma_corp-card': ProformaCorpCard,
        'proforma_staff-card': ProformaStaffCard,
        'marketing_campaign-card': MarketingCampaignCard,
        'annotation-card': AnnotationCard,
        'last_object-card': LastObjectCard,
        'bank_account_balance-card': BankAccountBalanceCard,
        'invoice_unreconciled-card': InvoiceUnreconciledCard,
        'my_activities-card': MyActivitiesCard,
        'cumulio-card': ReportCard,
        RecapDialog,
    },
    data() {
        return {
            min: 1,
            max: 0, // is set from response.metadata
            reportCardLimit: 0, // is set from response.metadata
            defaultOpenedTab: '', // is set from response.metadata
            gutter: 32,
            offsetMenu: 245,
            screenWidth: {
                sm: 1600,
                xs: 1440,
            },
            cols: { sm: 4, xs: 3 },
            col: 3,
            openPanelSettings: false,
            layout: [],
            index: 0,
            cards: [],
            snapLayout: [],
            snapCards: [],
            monitoredCards: [],
            maxMonitoredCards: 0,
            dashboard_available_cards: [],
            dashboard_has_stats: '',
            loading: true,
            error: false,
            timeout: null,
            dashboardManagementLabel: window.i18n.__('Gérer le tableau de bord'),
            isRecapDialogVisible: true,
        };
    },
    computed: {
        breakpoints() {
            const breakpoints = {};
            _.each(this.screenWidth, (width, index) => {
                breakpoints[index] = this.calcBreakpoints(width);
            });
            return breakpoints;
        },
        cardsForSettings() {
            const cardsForSettings = [];
            _.each(this.dashboard_available_cards, (card) => {
                const item = {
                    label: card.name,
                    context: card.context,
                    category: card.category,
                    active: !!this.getCard(card.type),
                    type: card.type,
                    has_to_upgrade: card.upgrade,
                };
                cardsForSettings.push(item);
            });

            return cardsForSettings;
        },
        showRecapDialog() {
            const canShowRecapDialog = localStorage.getItem('canShowRecapDialog');
            return this.isAdmin && Feature.isEnabled('recap-2024') && !canShowRecapDialog;
        },
        ...mapState({
            user: 'forename',
            isAdmin: 'isAdmin',
        }),
    },
    created() {
        this.$ajax.get(
            '/home/init',
            {},
            (response) => {
                this.max = response.metadata.nb_max_cards;
                this.reportCardLimit = response.metadata.nb_max_report_cards;
                this.defaultOpenedTab = response.metadata.default_tab;
                this.cards = response.dashboard_cards;
                this.dashboard_available_cards = response.dashboard_available_cards;
                this.dashboard_has_stats = response.dashboard_has_stats;
                this.timeout = response.metadata?.provider?.cumulio?.display_timeout;
                this.buildedLayout();
                this.maxMonitoredCards = this.layout
                    .map((item) => {
                        return item.type.indexOf('cumulio/');
                    })
                    ?.filter((item) => item >= 0)?.length;
                this.loading = false;
            },
            () => {
                Push.error(this.$l10n.__("Vous n'avez pas les droits nécessaires"));
                this.error = true;
                this.loading = false;
            },
        );
    },
    methods: {
        resizeEvent() {
            EventBus.emit('doLayoutCard');
            this.saveLayout();
        },
        getCard(cardType) {
            const finded = this.cards.find((card) => {
                return card.settings.type === cardType;
            });
            return finded;
        },
        applyButton() {
            this.openPanelSettings = false;
        },
        calcBreakpoints(width) {
            return width - this.offsetMenu;
        },
        removeItem(cardType, context, localMethod = true) {
            if (this.layout.length <= this.min) {
                Push.error(
                    this.$l10n.__(
                        'Vous devez sélectionner au moins une carte sur votre tableau de bord.',
                    ),
                    this.$l10n.__('Limite atteinte'),
                );
                return;
            }
            if (localMethod) {
                const cardSetings = this.getCard(cardType);
                if (cardSetings) {
                    const card = {
                        active: false,
                        category: cardSetings.settings.category,
                        context: cardSetings.settings.staff,
                        label: cardSetings.name,
                        type: cardSetings.settings.type,
                    };
                    EventBus.emit('cardSettingPanel:remove', card);
                }
            }
            if (context !== 'settings') {
                EventBus.emit('card-removed', cardType);
            }

            const removedCard = this.cards.findIndex((card) => {
                return card.settings.type === cardType;
            });
            this.cards.splice(removedCard, 1);

            const removedIndex = this.layout.findIndex((item) => {
                return item.type === cardType;
            });
            this.layout.splice(removedIndex, 1);
            this.saveLayout();
        },
        addItem(cardType) {
            const dataCard = this.dashboard_available_cards.find((card) => {
                return card.type === cardType;
            });

            this.cards.unshift({
                name: dataCard.name,
                settings: {
                    type: cardType,
                },
                customSettings: {},
            });

            const item = {
                x: 0,
                y: 0,
                w: dataCard.width,
                h: dataCard.height,
                minw: dataCard.minWidth,
                maxw: dataCard.maxWidth,
                minh: dataCard.minHeight,
                maxh: dataCard.maxHeight,
                i: this.index + '',
                resizable: dataCard.resizable,
                type: cardType,
                context: dataCard.context,
                category: dataCard.category,
                name: dataCard.name,
                loading: true,
            };
            this.layout.push(item);
            this.index++;
            this.saveLayout();
        },
        togglePanelSettings() {
            this.openPanelSettings = !this.openPanelSettings;

            if (this.openPanelSettings) {
                this.snapLayout = _.cloneDeep(this.layout);
                this.snapCards = _.cloneDeep(this.cards);
            } else {
                this.cards = _.cloneDeep(this.snapCards);
                this.layout = JSON.parse(JSON.stringify(this.snapLayout));
                this.saveLayout();
            }
        },
        layoutUpdated: _.debounce(function () {
            this.saveLayout();
        }, 100),
        getTypeCard(cardType) {
            return cardType.split('/')[0];
        },
        saveLayout: _.debounce(function () {
            window.SellsyJS.WebServiceCaller.callWebService({
                route: '/home/save',
                data: this.layout,
                method: 'post',
                success_callback: function (response) {
                    if (response.result === 'failed') {
                        Push.error(
                            this.$l10n.__(
                                "Une erreur est survenue pendant l'enregistrement, veuillez ré-essayer dans quelques minutes.",
                            ),
                            false,
                        );
                    }
                }.bind(this),
                error_callback: function () {
                    Push.error(
                        this.$l10n.__(
                            "Une erreur est survenue pendant l'enregistrement, veuillez ré-essayer dans quelques minutes.",
                        ),
                        false,
                    );
                }.bind(this),
            });
        }, 900),
        buildedLayout() {
            const width = this.calcBreakpoints($(document).width());
            let currentBreakpoint = 'xs';

            // Get right breakpoint for current screen size
            _.each(this.breakpoints, (breakpointWidth, breakpointName) => {
                if (width >= breakpointWidth && currentBreakpoint === 'xs') {
                    currentBreakpoint = breakpointName;
                }
            });
            this.col = this.cols[currentBreakpoint];

            this.computeLayout();
        },
        computeLayout() {
            // Used at first time when cards have no position or for force recompute
            let computedX = 0;
            let computedY = 0;

            _.each(this.cards, (card) => {
                if (parseInt(computedX) + parseInt(card.settings.width) > parseInt(this.col)) {
                    computedY += 1;
                    computedX = 0;
                }

                const layoutItem = {
                    x: parseInt(card.settings.position ? card.settings.position.x : computedX),
                    y: parseInt(card.settings.position ? card.settings.position.y : computedY),
                    w: parseInt(card.settings.width),
                    h: parseInt(card.settings.height),
                    minw: parseInt(card.settings.minWidth),
                    minh: parseInt(card.settings.minHeight),
                    maxw: parseInt(card.settings.maxWidth),
                    maxh: parseInt(card.settings.maxHeight),
                    resizable: Boolean(card.settings.resizable),
                    i: this.index,
                    type: card.settings.type,
                };

                this.layout.push(layoutItem);

                if (!card.settings.position) {
                    card.settings.position = {};
                }

                card.settings.position.x = layoutItem.x;
                card.settings.position.y = layoutItem.y;

                computedX = parseInt(computedX) + parseInt(card.settings.width);

                this.index++;
            });

            this.cards = this.cards.sort(function (a, b) {
                return (
                    a.settings.position.y - b.settings.position.y ||
                    a.settings.position.x - b.settings.position.x
                );
            });
        },
        cardFetchedEvent(type, cardData) {
            const cardIndex = this.cards.findIndex((card) => {
                return card.settings.type === type;
            });
            this.cards[cardIndex].content = cardData.content;
            this.cards[cardIndex].settings = Object.assign(
                this.cards[cardIndex].settings,
                cardData.settings,
            );
            this.cards[cardIndex].name = cardData.name;
            this.cards[cardIndex].customSettings = cardData.customSettings;
            this.$forceUpdate();
        },

        setMonitoredReportCards(card) {
            const cardData = {
                dashboard: card.dashboard,
                start: card.start,
                end: card.end,
                display: card.display,
            };
            cardData.display = this.error ? this.error : card.display;
            if (card?.type === 'cumulio') {
                this.monitoredCards.push(cardData);
            }
            if (this.monitoredCards.length === this.maxMonitoredCards) {
                this.monitoringCard();
            }
        },

        monitoringCard() {
            SellsyJS.WebServiceCaller.post('/home/monitoring', {}, this.monitoredCards);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';
@import '~@sellsy/styleguide/src/_mixins';

.dashboard-root {
    max-width: 1640px;
    margin: auto;
}

#dashboard-header {
    display: flex;
}
</style>
